/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo } from 'react';
// import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { useTranslation } from 'react-i18next';
// import styled from 'styled-components';
import { gql, useMutation } from '@apollo/client';

import useNavigation from '../hooks/useNavigation';
import useSearch from '../hooks/useSearch';
import useTransformGatsbyData from '../hooks/useTransformGatsbyData';
import useGetSeoCategory from '../hooks/data/useGetSeoCategory';

import { Category } from '../ui/templates/category/Category';
import { removeHTMLAndLineBreak, createUrlPath } from '../utils/utils';
import { isLocaleSweden } from '../utils/locale';
import { sortCategoriesByTierOrName } from '../utils/sharedFunc';

import useWithGA from '../hooks/useWithGA';
import useWithCategory from '../hooks/useWithCategory';
import useGetCategoryData from '../hooks/data/useGetCategoryData';
import useStrapiPageData from '../hooks/data/useStrapiPageData';
import MetatagsForLayout from '../components/MetatagsForLayout';

import { getCategoryTexts } from '../utils/sharedFunc';
import { Robot } from '../../static/icons/robot';
// import { Finance } from '../../static/icons/finance';

import {
  PAGE_SYSTEMGUIDE,
  PAGE_SYSTEM,
  PAGE_INDEX,
  // PAGE_CATEGORIES_COMPARE_SYSTEM,
  PAGE_COMPARE_SYSTEM_STATIC,
  // PAGE_FINANCE_BUSINESS_LOAN,
} from '../constants/strapi';
import CONSTANTS_NUMBERS from '../constants/numbers';

/**
 *
  subCategories: allBwSubCategory(
      filter: { category: { categoryDbName: { eq: $category } } }
    ) {
      nodes {
        data
      }
    }

 */
export const query = graphql`
  query CategoryData(
    $category: String!
    $topCategory: String
    $pageBuildId: String!
    $systemsIds: [String]
  ) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    categoryData: bwCategory(id: { eq: $category }) {
      data
    }
    categoryDefaultData: bwSinglePage(id: { eq: "category-default" }) {
      data
    }
    systemsInCategory: allBwSystem(filter: { id: { in: $systemsIds } }) {
      nodes {
        data
      }
    }
    systemPropsInCategory: allBwSystemProp(
      filter: { category: { eq: $category } }
    ) {
      nodes {
        category
        dbName
        filter
      }
    }
    bwPageBuildData(id: { eq: $pageBuildId }) {
      data
    }
    topCategoryData: bwTopCategory(id: { eq: $topCategory }) {
      data
    }
  }
`;

const ADD_COMPARISON = gql`
  mutation AddSystemComparison(
    $systemIds: [String!]!
    $systemId: String
    $systemCompareId: String
    $category: String
    $url: String
    $sessionId: String
  ) {
    addSystemComparison(
      systemIds: $systemIds
      systemId: $systemId
      systemCompareId: $systemCompareId
      category: $category
      url: $url
      sessionId: $sessionId
    ) {
      result
    }
  }
`;

const relatedCategoryList = ({ topCategory, category }) => {
  if (!topCategory?.data?.mainCategories || !category) {
    return null;
  }

  const sortedTopCategories = sortCategoriesByTierOrName(
    topCategory.data.mainCategories
  );

  return sortedTopCategories.map((catObj) => {
    const { categoryName: catName, categoryDbName, slugi18n } = catObj;
    const isActiveCat = categoryDbName === category;

    if (isActiveCat) {
      return {
        id: categoryDbName,
        name: catName,
        url: null,
      };
    }

    return {
      id: categoryDbName,
      name: catName,
      url: createUrlPath([slugi18n]),
    };
  });
};

const subCategoryList = ({ subCategories, categoryUrl: mainCategoryUrl }) => {
  if (!subCategories || !subCategories.length) {
    return null;
  }

  return subCategories.map((subCat) => {
    const { name, slugi18n, id } = subCat;

    return {
      id,
      name,
      url: createUrlPath([mainCategoryUrl, slugi18n]),
    };
  });
};

const CategoryTemplatePage = ({ location, data, pageContext }) => {
  const { getCategoryData } = useGetCategoryData();
  const bwPageBuildData = useTransformGatsbyData(data.bwPageBuildData);
  const { siteUrls } = bwPageBuildData.pageData;

  const { menu, footer } = useNavigation(siteUrls, location);
  const search = useSearch();
  const { locale, category } = pageContext;
  const { trackOutboundLink, savePageView, handleDataLayer } = useWithGA();
  const { t } = useTranslation();

  const { topCategoryData } = data;

  const categoryData = useTransformGatsbyData(data.categoryData);
  const systemsInCategory = useTransformGatsbyData(data.systemsInCategory);
  const systemPropsInCategory = useTransformGatsbyData(
    data.systemPropsInCategory
  );

  const {
    compare,
    filters,
    activeFilters,
    allSystems,
    systems,
    sorting,
    handleCompare,
    handleFilterChange,
    handleEmptyFilterItem,
    handleSortChange,
    emptyFilter,
  } = useWithCategory(category, systemsInCategory, systemPropsInCategory);

  const [systemguidePageUrl] = useStrapiPageData(PAGE_SYSTEMGUIDE);
  const [systemPageUrl] = useStrapiPageData(PAGE_SYSTEM);
  // const [compareSystemsPageUrl] = useStrapiPageData(
  //   PAGE_CATEGORIES_COMPARE_SYSTEM
  // );
  const [compareSystemsStaticPageUrl] = useStrapiPageData(
    PAGE_COMPARE_SYSTEM_STATIC
  );
  // const [financeBusinessLoanPageUrl] = useStrapiPageData(
  //   PAGE_FINANCE_BUSINESS_LOAN
  // );

  const [
    createAddComparisonFn,
    // {
    //   data: createAddComparisonData,
    //   loading: createAddComparisonLoading,
    //   error: createAddComparisonError,
    // },
  ] = useMutation(ADD_COMPARISON);

  // let acceptedCookie = true;
  // if (typeof window !== `undefined`) {
  //   acceptedCookie = localStorage.getItem('cookieAccepted') === 'true' || false;
  // }
  useEffect(() => {
    savePageView({ type: 'CATEGORY', ids: category });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    categoryDbName,
    title,
    categoryName,
    slugi18n: categoryUrl,
    // topCategory,
    subCategories,
    whatIsTitle,
    whatIsText,
    howBestTitle,
    howBestText,
    compareTitle,
    compareText,
    whyTitle,
    whyText,
    priceTitle,
    priceText,
    howBuyTitle,
    howBuyText,
    faq1Title,
    faq1Text,
    faq2Title,
    faq2Text,
    topSectionTitle,
    topSectionText,
    articleTitle,
    articleText,
    textSections,
  } = categoryData;

  const { categoryHasGuide } = getCategoryData(category);
  const categoryGuideUrl = categoryHasGuide
    ? `/${categoryUrl}${systemguidePageUrl}`
    : `${systemguidePageUrl}`;

  const { texts, linkTextToTextUnder, linkIdToTextUnder } = getCategoryTexts(
    {
      whatIsTitle,
      whatIsText,
      howBestTitle,
      howBestText,
      compareTitle,
      compareText,
      whyTitle,
      whyText,
      priceTitle,
      priceText,
      howBuyTitle,
      howBuyText,
      faq1Title,
      faq1Text,
      faq2Title,
      faq2Text,
      topSectionTitle,
      topSectionText,
      articleTitle,
      articleText,
      textSections,
    },
    categoryHasGuide
  );

  // const compareAvailable = compare?.length < 2;

  const isSweden = isLocaleSweden(locale);

  const systemPropsNames = systemPropsInCategory.map((obj) => obj.dbName);
  const prodFuncDesc = systemPropsNames
    .map((prodKey) => ({
      id: prodKey,
      title: t([
        `prodFunc.${category}.${prodKey}`,
        `prodFunc.common.${prodKey}`,
      ]),
      text: t(`prodFuncDesc.${category}.${prodKey}`),
    }))
    .filter(
      (item) => !item.text.includes('prodFuncDesc') && item.text?.length > 10
    )
    .sort((a, b) => {
      const nameA = a.title.toUpperCase();
      const nameB = b.title.toUpperCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });

  const visibleSystems = systems.length > 0 ? systems : allSystems;
  const noResults = systems.length === 0;

  const relatedCategoriesArr = useMemo(
    () => relatedCategoryList({ topCategory: topCategoryData, category }),
    [topCategoryData, category]
  );
  const subCategoriesArr = useMemo(
    () =>
      subCategoryList({
        subCategories,
        categoryUrl,
      }),
    [subCategories, categoryUrl]
  );

  let sectionTopCard = null;

  // Temperary removed untill we decide what to do with this
  // if (categoryDbName === 'COMPANY_BANK') {
  //   sectionTopCard = {
  //     title: 'Företagslån',
  //     description:
  //       'Söker du ett lån till ditt företag? Vi hjälper dig hitta bästa lösningen för dig!',
  //     url: financeBusinessLoanPageUrl,
  //     icon: <Finance />,
  //     handleClick: () => {
  //       handleDataLayer({
  //         event_name: 'to_systemguide',
  //         page_type: 'Category page',
  //         system_category: 'FINANCE_BUSINESS_LOAN',
  //       });
  //       // TODO: Implement this all the way
  //       // handleDataLayer({
  //       //   event_name: 'cta',
  //       //   page_type: 'Category page',
  //       //   system_category: 'FINANCE_BUSINESS_LOAN',
  //       //   type: 'finance',
  //       // });
  //     },
  //   };
  // } else
  if (categoryHasGuide) {
    sectionTopCard = {
      title: isSweden
        ? t('common.helpSystemGuide')
        : t('common.trySystemGuide'),
      description: t('common.systemGuideAlgorithmsText', {
        number: CONSTANTS_NUMBERS.guideRuns,
      }),
      url: categoryGuideUrl,
      icon: <Robot color="#297FFF" width="5" />,
      label: t('common.systemGuide'),
      handleClick: () => {
        handleDataLayer({
          event_name: 'to_systemguide',
          page_type: 'Category page',
          system_category: category || '',
        });
        // TODO: Implement this all the way
        // handleDataLayer({
        //   event_name: 'cta',
        //   page_type: 'Category page',
        //   system_category: category || '',
        //   type: 'systemguide',
        // });
      },
    };
  }

  const handleAddComparison = (comparedSystems, url) => {
    const systemIds = comparedSystems.map((sObj) => sObj.id);

    createAddComparisonFn({
      variables: {
        systemIds,
        systemId: systemIds?.[0],
        systemCompareId: systemIds?.[1],
        category,
        url,
        sessionId: sessionStorage.getItem('sessionId') || null,
      },
    });
  };

  return (
    <Category
      hero={{
        title,
        card: sectionTopCard,
      }}
      breadcrumbs={[
        {
          title: t('common.home'),
          href: '/',
        },
        {
          title: categoryName,
          // href: `/${categoryUrl}/`,
        },
      ]}
      sections={[
        {
          sectionType: 'single',
          component: {
            componentType: 'sectionOne',
            type: 'sectionOne',
            // cardSystemGuide: sectionTopCard,
            cardSubCategories: {
              title: t('common.subCategoriesTo', { category: categoryName }),
              // description: '',
              links: subCategoriesArr,
              handleClick: (subCatName) => {
                handleDataLayer({
                  event_name: 'to_subcategory',
                  page_type: 'Category page',
                  system_category: category || '',
                  sub_category: subCatName || '',
                });
                // TODO: Implement this all the way
                // handleDataLayer({
                //   event_name: 'cta',
                //   page_type: 'Category page',
                //   system_category: category || '',
                //   type: 'systemguide',
                // });
              },
            },
            linkTextToTextUnder,
            linkIdToTextUnder,
            componentData: texts.over,
          },
        },
        {
          sectionType: 'single',
          component: {
            componentType: 'sectionSystemList',
            type: 'sectionSystemList',
            componentData: {
              totalSystems: systemsInCategory.length,
              categoryName,
              systemPageUrl,
              systems: visibleSystems,
              noResults,
              relatedCategories: relatedCategoriesArr,
              subCategories: subCategoriesArr,
              compare,
              filters,
              activeFilters,
              allSystems,
              sorting,
              handleCompare,
              handleFilterChange,
              handleEmptyFilterItem,
              handleSortChange,
              emptyFilter,
              category: categoryDbName,
              categoryId: categoryDbName.toLowerCase(),
              // compareSystemsPageUrl,
              compareSystemsStaticPageUrl,
              handleAddComparison,
            },
          },
        },
        {
          sectionType: 'single',
          component: {
            componentType: 'sectionThree',
            type: 'sectionThree',
            linkIdToTextUnder,
            categoryGuideUrl,
            componentData: texts.under,
          },
        },
        {
          sectionType: 'single',
          component: {
            componentType: 'sectionAccordion',
            type: 'sectionAccordion',
            // label: 'Lorem Ipsum',
            title: t(
              [
                `cat.${category}.productFuncTitle`,
                `cat.common.productFuncTitle`,
              ],
              { category }
            ),
            componentData: prodFuncDesc,
          },
        },
      ]}
      t={t}
      locale={locale}
      menu={menu}
      footer={footer}
      siteUrls={siteUrls}
      location={location}
      search={search}
      trackOutboundLink={trackOutboundLink}
    />
  );
};

CategoryTemplatePage.propTypes = {};

export default CategoryTemplatePage;

export function Head({ data, location, pageContext }) {
  const { locale } = pageContext;
  const { site } = data;

  const { t } = useTranslation();
  const [homePageUrl, homePageData] = useStrapiPageData(PAGE_INDEX);

  const categoryData = useTransformGatsbyData(data.categoryData);
  const categoryDefaultData = useTransformGatsbyData(data.categoryDefaultData);
  const bwPageBuildData = useTransformGatsbyData(data.bwPageBuildData);
  const systemsInCategory = useTransformGatsbyData(data.systemsInCategory);

  const {
    categoryName,
    seo,
    faq1Title,
    faq1Text,
    faq2Title,
    faq2Text,
    whatIsTitle,
    whatIsText,
    howBestTitle,
    howBestText,
    compareTitle,
    compareText,
    whyTitle,
    whyText,
    priceTitle,
    priceText,
    howBuyTitle,
    howBuyText,
  } = categoryData;

  const { seo: seoIndexPage } = homePageData;
  const { siteUrls } = bwPageBuildData.pageData;
  const seoCategory = useGetSeoCategory({
    categoryData,
    categoryDefaultData,
    systemCount: systemsInCategory?.length || 0,
    country: t(`shared.localeToCountry.${locale}`),
    siteUrl: site.siteMetadata.siteUrl,
  });

  const ldJsonArray = [];
  const faqForLdJson = [];
  const homeTextForBreadCrumb = t('common.home');

  ldJsonArray.push({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: homeTextForBreadCrumb,
        item: {
          '@id': site.siteMetadata.siteUrl,
          name: seoIndexPage.metaTitle,
          description: seoIndexPage.metaDescription,
          url: site.siteMetadata.siteUrl,
          // sameAs: [
          //   'https://businesswith.se',
          //   'https://businesswith.no',
          //   'https://businesswith.dk',
          // ],
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: {
          '@id': site.siteMetadata.siteUrl + location.pathname,
          name: categoryName,
          description: seoCategory?.category?.metaDescription,
          url: site.siteMetadata.siteUrl + location.pathname,
        },
      },
    ],
  });

  let hasNewFields = false;

  if (
    whatIsTitle ||
    howBestTitle ||
    compareTitle ||
    whyTitle ||
    priceTitle ||
    howBuyTitle
  ) {
    hasNewFields = true;
    if (whatIsTitle && whatIsText !== '') {
      faqForLdJson.push({
        '@type': 'Question',
        name: whatIsTitle,
        acceptedAnswer: {
          '@type': 'Answer',
          text: removeHTMLAndLineBreak(whatIsText),
        },
      });
    }
    if (howBestTitle && howBestText !== '') {
      faqForLdJson.push({
        '@type': 'Question',
        name: howBestTitle,
        acceptedAnswer: {
          '@type': 'Answer',
          text: removeHTMLAndLineBreak(howBestText),
        },
      });
    }
    if (compareTitle && compareText !== '') {
      faqForLdJson.push({
        '@type': 'Question',
        name: compareTitle,
        acceptedAnswer: {
          '@type': 'Answer',
          text: removeHTMLAndLineBreak(compareText),
        },
      });
    }
    if (whyTitle && whyText !== '') {
      faqForLdJson.push({
        '@type': 'Question',
        name: whyTitle,
        acceptedAnswer: {
          '@type': 'Answer',
          text: removeHTMLAndLineBreak(whyText),
        },
      });
    }
    if (priceTitle && priceText !== '') {
      faqForLdJson.push({
        '@type': 'Question',
        name: priceTitle,
        acceptedAnswer: {
          '@type': 'Answer',
          text: removeHTMLAndLineBreak(priceText),
        },
      });
    }
    if (howBuyTitle && howBuyText !== '') {
      faqForLdJson.push({
        '@type': 'Question',
        name: howBuyTitle,
        acceptedAnswer: {
          '@type': 'Answer',
          text: removeHTMLAndLineBreak(howBuyText),
        },
      });
    }
  }

  if (!hasNewFields && faq1Title && faq1Text !== '') {
    faqForLdJson.push({
      '@type': 'Question',
      name: faq1Title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: removeHTMLAndLineBreak(faq1Text),
      },
    });
  }

  if (!hasNewFields && faq2Title && faq2Text !== '') {
    faqForLdJson.push({
      '@type': 'Question',
      name: faq2Title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: removeHTMLAndLineBreak(faq2Text),
      },
    });
  }

  if (faqForLdJson.length > 0) {
    ldJsonArray.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqForLdJson,
    });
  }

  return (
    <>
      <MetatagsForLayout
        title={seoCategory.category?.metaTitle}
        description={seoCategory.category?.metaDescription}
        pathname={location.pathname}
        locale={locale}
        siteUrls={siteUrls}
      />
      <script type="application/ld+json">{JSON.stringify(ldJsonArray)}</script>
    </>
  );
}
